/* eslint-disable lingui/no-unlocalized-strings -- Native DEXPI relationship identifiers. */
import type { CanvasPoint, NodeOverlay } from './canvasTypes'
import { pipeDirection } from './flowGraph'
import type { FlowPath, FlowState } from './flowTypes'
import { polygonContains } from './graphGeometry'
import type { GraphNode } from './graphTypes'
import { polylineDistance } from './polylineGeometry'

function overlayTouchesPoint(
  overlay: NodeOverlay,
  point: CanvasPoint
): boolean {
  if (
    overlay.geometry.type === 'polygon' &&
    polygonContains(overlay.geometry.points, point)
  )
    return true
  const points =
    overlay.geometry.type === 'polygon'
      ? [...overlay.geometry.points, overlay.geometry.points[0]]
      : overlay.geometry.points
  return polylineDistance(points, point, { scale_x: 1, scale_y: 1 }) <= 2
}

function endOverlays(
  pipe: GraphNode,
  end: 'Source' | 'Target',
  overlays: NodeOverlay[]
): NodeOverlay[] {
  const node = pipe.getUniqueTargetNode(`${end}Node`)
  const item = pipe.getUniqueTargetNode(`${end}Item`)
  const appearances = overlays.filter((overlay) => overlay.node.id === node?.id)
  return appearances.length
    ? appearances
    : overlays.filter((overlay) => overlay.node.id === item?.id)
}

function pathOrientation(
  pipe: GraphNode,
  overlay: NodeOverlay,
  overlays: NodeOverlay[]
): 'forward' | 'reverse' | undefined {
  const points = overlay.geometry.points
  const start = points[0]
  const end = points[points.length - 1]
  const source = endOverlays(pipe, 'Source', overlays)
  const target = endOverlays(pipe, 'Target', overlays)
  const forward =
    source.some((item) => overlayTouchesPoint(item, start)) &&
    target.some((item) => overlayTouchesPoint(item, end))
  const reverse =
    source.some((item) => overlayTouchesPoint(item, end)) &&
    target.some((item) => overlayTouchesPoint(item, start))
  return forward === reverse ? undefined : forward ? 'forward' : 'reverse'
}

export function projectFlowPaths(
  pageId: string,
  states: Map<string, FlowState>,
  overlays: NodeOverlay[]
): FlowPath[] {
  const paths: FlowPath[] = []
  const local = overlays.filter((overlay) => overlay.pageId === pageId)
  for (const overlay of local) {
    const state = states.get(overlay.node.id)
    if (!state) continue
    const direction = pipeDirection(overlay.node)
    if (overlay.geometry.type !== 'polyline') continue
    const order =
      direction === 'forward'
        ? pathOrientation(overlay.node, overlay, local)
        : undefined
    paths.push({
      overlayId: overlay.overlayId,
      nodeId: overlay.node.id,
      geometry:
        order === 'reverse'
          ? { type: 'polyline', points: [...overlay.geometry.points].reverse() }
          : overlay.geometry,
      state,
      directed: Boolean(order) && state === 'enabled',
    })
  }
  return paths
}
